import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useSpinoza } from '../../context/SpinozaContext';

/**
 * SpinozaBubble — speech bubble above-right of avatar.
 * Fine-line square card, max-width 360px.
 * Typewriter text effect. "..." while thinking.
 * Signals isTyping to SpinozaContext so the avatar mouth animation
 * is fast while typing and slow when idle.
 */
const SpinozaBubble = ({ text, isThinking, onDismiss }) => {
  const [displayed, setDisplayed] = useState('');
  const intervalRef = useRef(null);
  const { setIsTyping } = useSpinoza();

  useEffect(() => {
    if (isThinking || !text) {
      setDisplayed('');
      setIsTyping(false);
      return;
    }

    setDisplayed('');
    setIsTyping(true);
    let idx = 0;
    intervalRef.current = setInterval(() => {
      idx++;
      if (idx <= text.length) {
        setDisplayed(text.slice(0, idx));
      } else {
        clearInterval(intervalRef.current);
        setIsTyping(false);
      }
    }, 20);

    return () => {
      clearInterval(intervalRef.current);
      setIsTyping(false);
    };
  }, [text, isThinking, setIsTyping]);

  if (!text && !isThinking) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="relative bg-white border border-zinc-300 p-3 shadow-sm"
      style={{ maxWidth: 360 }}
    >
      {/* Dismiss button */}
      <button
        onClick={onDismiss}
        className="absolute top-1 right-1 p-0.5 text-zinc-400 hover:text-black transition-colors"
        aria-label="Dismiss"
      >
        <X className="w-3 h-3" />
      </button>

      {isThinking ? (
        <span className="font-mono text-xs text-zinc-500">
          <span className="spinoza-cursor">...</span>
        </span>
      ) : (
        <p className="font-sans text-xs text-zinc-700 leading-relaxed pr-4">
          {displayed}
          {displayed.length < (text?.length || 0) && (
            <span className="spinoza-cursor">|</span>
          )}
        </p>
      )}
    </div>
  );
};

export default SpinozaBubble;
